<script lang="ts" context="module">
</script>

<script lang="ts">
  import { Accordion, AccordionItem, Button, FormGroup, TextArea, TextInput } from 'carbon-components-svelte';
  import Help from 'carbon-icons-svelte/lib/Help.svelte';
  import 'carbon-components-svelte/css/g80.css';

  import BaseConfig from './lib/BaseConfig.svelte';
  import CopyButton from './lib/CopyButton.svelte';
  import InteractiveConfig from './lib/InteractiveConfig.svelte';
  import PlayerSetup from './lib/PlayerSetup.svelte';
  import Presets from './lib/Presets.svelte';
  import TestConfig from './lib/TestConfig.svelte';
  import VideoElements from './lib/VideoElements.svelte';
  import {
    type AttributeOptions,
    type BuildEnv,
    camelToSnakeCase,
    type EmbedValue,
    findOr,
    getDefaultBuild,
    getInitBuild,
    initAttributeOptions,
    numberOr,
    snakeCaseAttributes,
    type SrcMethod,
    type TestingAttributes,
    type TestingQueryAttributes,
  } from './modules/attributes';

  const params = new URL(window.location.href).searchParams;
  let attributes: AttributeOptions = $state(initAttributeOptions(params));
  let testingQueryAttributes: TestingQueryAttributes = $state({
    seekDuration: numberOr({
      params,
      name: 'seekDuration',
      fallback: null,
    }),
    seekDurationMax: numberOr({
      params,
      name: 'seekDurationMax',
      fallback: 30,
    }),
    seekDurationMin: numberOr({
      params,
      name: 'seekDurationMin',
      fallback: 5,
    }),
  });

  let srcMethod: SrcMethod = $state('module');
  let embedValue = $state(
    findOr<EmbedValue>({
      arr: ['iframe', 'alt', 'script'],
      value: 'embed',
      fallback: 'iframe',
    })
  );

  let buildEnv: BuildEnv = $state(getInitBuild({ params, defaultBuild: getDefaultBuild() }));
</script>

<main>
  <div style="width:100%;display:flex;flex-flow: row nowrap;gap:16px;padding: 0 1em; justify-content: space-between;">
    <PlayerSetup bind:embedValue bind:srcMethod bind:buildEnv />
    <div style="display:flex;flex-flow: row nowrap;gap:16px;">
      <Button
        class="bx--copy-btn bx--copy"
        iconDescription="Open Documentation"
        target="_blank"
        href="https://tinyurl.com/ms24hasr"
        icon={Help}
        style="min-height: 2.5rem;" />
      <CopyButton attributes={{ ...attributes, ...testingQueryAttributes }} {embedValue} {buildEnv} />
    </div>
  </div>
  <Accordion size="xl">
    <AccordionItem open title="Base Config (Manifest, Environment, Type)">
      <div style="width:100%;display:flex;flex-flow: row nowrap;gap:16px;">
        <div style="flex-basis:60%;padding: 0 1em;">
          <Presets bind:attributes />
          <FormGroup legendText="Manifest">
            <TextArea
              rows={2}
              data-testid="string--manifest"
              bind:value={attributes.manifest}
              placeholder="Enter manifest..." />
          </FormGroup>
          <FormGroup legendText="Encoded Manifest">
            <TextArea
              data-testid="string--encoded-manifest"
              rows={2}
              bind:value={attributes.encodedManifest}
              placeholder="Enter encoded manifest..." />
          </FormGroup>
          <FormGroup legendText="Embed Id">
            <TextInput
              data-testid="string--embed-id"
              size="sm"
              bind:value={attributes.id}
              placeholder="Enter embed id..." />
          </FormGroup>
          <FormGroup legendText="Player Profile Id">
            <TextInput
              data-testid="string--embed-id"
              size="sm"
              bind:value={attributes.playerProfileId}
              placeholder="Enter player profile id..." />
          </FormGroup>
        </div>

        <div style="flex-basis:40%;padding: 1em 1em 0 1em;">
          <BaseConfig bind:attributes />
        </div>
      </div>
    </AccordionItem>
  </Accordion>
  <div style="padding: 2em 0;">
    <div style="width:100%;display:flex;flex-flow: row nowrap;gap:16px;">
      <div style="width:50%;padding: 1em;">
        <VideoElements attributes={{ ...attributes, ...testingQueryAttributes }} {buildEnv} {embedValue} {srcMethod} />
      </div>
      <div role="group" aria-label="boolean params" style="width:50%;padding: 1em;">
        <InteractiveConfig bind:attributes />
      </div>
    </div>
  </div>
  <TestConfig bind:attributes bind:testingQueryAttributes />
</main>

<style>
  :global(.bx--accordion__content) {
    padding-right: 0 !important;
    padding-bottom: 0 !important;
  }
  :global(.bx--tile) {
    padding-top: 0.5rem !important;
    padding-bottom: 0.5rem !important;
    padding-left: 0.5rem !important;
    min-height: 3rem;
    display: flex;
    align-items: center;
  }
  :root {
    font-family:
      -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue',
      sans-serif;
  }

  main {
    padding: 1em;
    margin: 0 auto;
  }
</style>
